import { jWebReady } from '@jarvis/jweb-core';
import { ANDROID, IOS, PUBLISHER_ID } from './constants';
import { registerServiceInstanceClosedListener } from './listeners';

let publisherSingleton;

export const publishSimpleUIEvent = async (name, data) => {
  console.log('events::publishEvent: %o: %o', name, data);

  const publisher = await createPublisher();

  if (publisher && publisher.errorType !== undefined) {
    console.error('Error creating ', result);
  }

  if (publisher && publisher.publish) {
    publisher
      .publish(name, data)
      .then(result => {
        if (result !== undefined) {
          console.log('Unable to publish event');
        } else {
          console.log('Event published successfully');
        }
      })
      .catch(error => console.error('events::publishEvent: %o', error));
  }
};

export const createSubscriber = async () => {
  const jweb = await jWebReady;

  if (jweb && jweb.isNative) {
    const { EventService } = jweb.Plugins;

    try {
      const subscriber = await EventService.createSubscriber();

      if (subscriber.errorType !== undefined) {
        console.error('events::createSubscriber: %o', subscriber);
      }
      return subscriber;
    } catch (e) {
      console.log('error creating subscriber %o', e);
    }
  }
};

export const destroyPublisher = () => {
  if (publisherSingleton.destroy) {
    console.log('Destroying singleton');
    publisherSingleton.destroy().then(result => {
      if (result.errorType !== undefined) {
        console.error('error::destroyPublisher: %o', result);
      }

      publisherSingleton = {};
    });
  }
};

export const createPublisher = async () => {
  const jweb = await jWebReady;

  if (jweb && jweb.isNative) {
    const { EventService } = jweb.Plugins;

    const options = {
      allowEventingFallback: true
    };

    if (publisherSingleton) {
      console.log('Using existing publisher');
      return publisherSingleton;
    }

    try {
      console.log('Creating publisher');
      publisherSingleton = EventService.createPublisher(`${PUBLISHER_ID}.${Date.now()}`, options);
      return publisherSingleton;
    } catch (e) {
      console.error('Error creating Publisher: %o', e);
    }
  }

  return Promise.resolve({
    publish: (eventName, eventData) => {
      console.info(eventName, eventData);
      return Promise.resolve();
    }
  });
};

/**
 * Send an event to Native Application.  Native Application must have a listener for the events sent.
 * @param {string} eventName Name and unique identifier for the event
 * @param {JSON} eventData The data to send to the Native Application
 */
export const sendEvent = async (eventName, eventData) => {
  try {
    const jweb = await jWebReady;

    if (jweb && jweb.isNative) {
      const { EventService } = jweb.Plugins;

      const publisherId = `com.hp.jarvis.cec.${eventName}.publisher.${Date.now()}`;

      const publisher = await EventService.createPublisher(publisherId);
      if (typeof publisher.errorType !== 'undefined') {
        console.error('Publisher creation error', publisher.errorType);
        return;
      }
      // Publishes event
      const publisherEvent = await publisher.publish(eventName, eventData);
      if (typeof publisherEvent.errorType !== 'undefined') {
        console.error('Publisher error:', publisherEvent.errorType);
        return;
      }
    } else {
      console.info(eventName, eventData);
    }
  } catch (error) {
    console.error(`SEND EVENT ERROR: ${error}`);
  }
};

/**
 * Checks the Native Application to determine if the user is signed in, then fetch the User Auth Token when user is signed in.
 * @param {boolean} refresh Token refresh
 * @returns {string} JWT Value or empty string (See getUserAuthToken for payload), if signed In and empty string if signed out.
 */
export const checkSignInAndGetUserAuthToken = async (refresh = true) => {
  try {
    const jweb = await jWebReady;

    if (jweb && jweb.isNative) {
      const { Auth } = jweb.Plugins;
      const isLoggedIn = await Auth.isLoggedIn();
      if (isLoggedIn.value) {
        return getUserAuthToken(refresh);
      }
    }
    return;
  } catch (error) {
    console.error(`SEND CHECK SIGN IN AUTH ERROR: ${error}`);
  }
};

/**
 * Fetches the Auth information from the Native App.
 * Payload {
 *    tokenValue: "[token]",
 *    account: {
 *        emailAddress: "[email address]",
 *        givenName: "[first name]",
 *        familyName: "[last name]",
 *        accountId: "[account id]",
 *    },
 *    expiresAt: "[2021-02-03T06:51:02.000Z]"
 * }
 * @param {boolean} refresh Token refresh
 * @returns {object} authInfo
 */
export const getUserAuthDetails = async (refresh = true) => {
  try {
    const jweb = await jWebReady;

    if (jweb && jweb.isNative) {
      const { Auth } = jweb.Plugins;
      const token = await Auth.getToken({
        tokenProviderOptions: {
          allowNetworkAccess: true,
          requireFreshToken: refresh,
          allowUserInteraction: false
        }
      });
      return token;
    }
    return {
      tokenValue: '',
      account: {}
    };
  } catch (error) {
    console.error(`SEND AUTH ERROR: ${error}`);
  }
};

/**
 * Fetches the Auth information from the Native App.  Parse out the token value from payload.
 * Payload {
 *    tokenValue: "[token]",
 *    account: {
 *        emailAddress: "[email address]",
 *        givenName: "[first name]",
 *        familyName: "[last name]",
 *        accountId: "[account id]",
 *    },
 *    expiresAt: "[2021-02-03T06:51:02.000Z]"
 * }
 * @param {boolean} refresh Token refresh
 * @returns {string} JWT Value or empty string
 */
export const getUserAuthToken = async (refresh = true) => {
  const authDetails = await getUserAuthDetails(refresh);
  return authDetails.tokenValue;
};

/**
 * Fetches the Auth information from the Native App.  Parse out the account details from payload.
 * Payload {
 *    tokenValue: "[token]",
 *    account: {
 *        emailAddress: "[email address]",
 *        givenName: "[first name]",
 *        familyName: "[last name]",
 *        accountId: "[account id]",
 *    },
 *    expiresAt: "[2021-02-03T06:51:02.000Z]"
 * }
 * @param {boolean} refresh Token refresh
 * @returns {object} account or empty object
 */
export const getUserAuthAccount = async (refresh = true) => {
  const authDetails = await getUserAuthDetails(refresh);
  return authDetails.account;
};

/**
 * Request for Device Information from the Native App.
 * Payload {
 *    "memUsed": "121262080",
 *    "diskFree": "249009405952",
 *    "diskTotal": "510922846208",
 *    "model": "[device model]",
 *    "osVersion": "[device operating system version]",
 *    "appVersion": "[application version]",
 *    "appBuild": "[application build number]",
 *    "platform": "[device platform]",
 *    "manufacturer": "[name of manufacturer]",
 *    "batteryLevel": "0.977795914716635",
 *    "isCharging": "[true|false]",
 *    "isVirtual": "[true|false]",
 *    "jWebViewVersion": "[jweb view version]"
 * }
 * @returns {JSON} Device Information
 */
export const getDeviceInfomation = async () => {
  try {
    const jweb = await jWebReady;

    if (jweb && jweb.isNative) {
      const { Device } = jweb.Plugins;
      return await Device.getInfo();
    }
    console.error('DEVICE INFO ERROR: JWeb is not ready for sending events.');
  } catch (error) {
    console.error(`DEVICE INFO ERROR: ${error}`);
  }
};

/**
 * Request for the Device Language and Country from the Native App.
 * @returns {string} Language and Country in the following format "lc-CC"
 */
export const getDeviceLanguageCountryCode = async () => {
  try {
    const jweb = await jWebReady;

    if (jweb && jweb.isNative) {
      const { Device } = jweb.Plugins;
      return (await Device.getLanguageCode()).value;
    }
    console.error('DEVICE LANGUAGE|COUNTRY ERROR: JWeb is not ready for sending events.');
  } catch (error) {
    console.error(`DEVICE LANGUAGE|COUNTRY ERROR: ${error}`);
  }
};

/**
 * Call Service Routing Plugin to obtain all available services.
 * @returns A list of services or empty array if there is an error or services are not available.
 */
const getAvailableServices = async () => {
  try {
    const jweb = await jWebReady;

    if (jweb && jweb.isNative) {
      const { ServiceRouting } = jweb.Plugins;
      const listOfServices = (await ServiceRouting.getServices()).services;
      if (listOfServices.hasOwnProperty('errorType')) {
        console.error(
          `SERVICE ROUTING AVAILABLE SERVICES ERROR: ${listOfServices.errorType} - ${listOfServices.message}`
        );
        return [];
      }
      return listOfServices;
    }
    console.error(
      'SERVICE ROUTING AVAILABLE SERVICES ERROR: JWeb is not ready for sending events.'
    );
  } catch (error) {
    console.error(`SERVICE ROUTING AVAILABLE SERVICES ERROR: ${error}`);
  }
};

/**
 * Returns the Service through Service Routing Plugin.
 * @param {string} serviceId Service ID
 * @param {string} containerViewType Container View Type from JWeb Plugin (fullScreen | determinedByService | modal)
 * @returns The requested Service if available.  Returns null, if not available.
 */
const getServiceIfAvailable = async serviceId => {
  const listOfServices = await getAvailableServices();
  const service = listOfServices.find(servicelist => servicelist.id === serviceId);
  if (service) {
    return service;
  }
  return null;
};

/**
 * Opens the URL in a full screen JWeb View.
 * @param {string} url The URL being launched
 * @param {boolean=} isBelowNavBar Activates the native app navigation bar (true | false)
 * @param {string=} containerViewType Container View Type from JWeb Plugin (fullScreen | determinedByService | modal)
 * @param {string=} containerCloseBehavior Container Close Behavior from JWeb Plugin (returnToCaller)
 */
export const launchURL = async (
  url,
  isBelowNavBar = false,
  useExternalBrowser = false,
  eventPostCallback,
  containerViewType = 'fullScreen',
  containerCloseBehavior = 'returnToCaller'
) => {
  try {
    const OPEN_URL_SERVICE = 'openUrl';
    const jweb = await jWebReady;

    if (jweb && jweb.isNative) {
      const { ServiceRouting } = jweb.Plugins;
      const getURLService = await getServiceIfAvailable(OPEN_URL_SERVICE, containerViewType);

      if (getURLService) {
        const serviceOptions = {
          serviceId: OPEN_URL_SERVICE,
          containerOptions: {
            containerViewOptions: {
              containerViewType,
              belowNavBar: isBelowNavBar
            },
            containerCloseBehaviorOptions: {
              closeBehavior: containerCloseBehavior
            }
          },
          serviceOptions: {
            url,
            useExternalBrowser
          }
        };

        const serviceLaunch = await ServiceRouting.launchService(serviceOptions);
        console.log('Service Routing Launched');

        if (process.env.NODE_ENV !== 'production') {
          console.log(`Service Routing: ${JSON.stringify(serviceOptions, null, 2)}`);
          console.log(`Service Routing Response: ${JSON.stringify(serviceLaunch, null, 2)}`);
        }

        if (serviceLaunch.hasOwnProperty('errorType')) {
          console.error(`LAUNCH URL ERROR: ${serviceLaunch.errorType} - ${serviceLaunch.message}`);
        } else {
          registerServiceInstanceClosedListener(serviceLaunch.eventPublisherId);
          eventPostCallback();
        }
      } else {
        console.error('LAUNCH URL ERROR: openUrl service not found.');
      }
    } else {
      window.location.assign(`${url}`);
    }
  } catch (error) {
    console.error(`LAUNCH URL ERROR: ${error}`);
  }
};

/**
 * Checks if the host is mobile platform.
 * @returns A flag determining if the host is mobile platform (Android || Ios)
 */

export const isMobileHost = async () => {
  try {
    const jweb = await jWebReady;
    if (jweb && jweb.isNative) {
      return jweb.platform === ANDROID || jweb.platform === IOS;
    }
    return false;
  } catch (error) {
    console.error(`Jweb platform ERROR: ${error}`);
    return false;
  }
};

/**
 * Return host platform.
 * @returns host platform
 */
export const getHost = async () => {
  try {
    const jweb = await jWebReady;
    if (jweb && jweb.isNative) {
      return jweb.platform;
    }
    return null;
  } catch (error) {
    console.error(`Jweb platform ERROR: ${error}`);
    return null;
  }
};
